import { Code, Cloud, GitBranch, Container, Shield, Zap, Server, Database, Terminal, Settings } from 'lucide-react';
import type { Product } from './types';

export const developerToolsProducts: Product[] = [
  {
    id: 'tailscale-networking',
    title: 'Tailscale',
    subtitle: 'Secure Network Connectivity',
    description: 'WireGuard-based secure networking solution that creates virtual private networks connecting devices across different networks seamlessly.',
    icon: Cloud,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#networking', '#security', '#vpn', '#infrastructure', '#remote-access'],
    features: [
      'Zero-config WireGuard VPN',
      'Cross-platform device connectivity',
      'Secure network access controls',
      'Real-time network monitoring',
      'Team collaboration features',
      'Enterprise-grade security',
      'UK data sovereignty compliance',
      'GDPR network data protection'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'supabase-platform',
    title: 'Supabase',
    subtitle: 'Open Source Firebase Alternative',
    description: 'Complete backend-as-a-service platform with Postgres database, authentication, instant APIs, edge functions, and real-time capabilities.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#backend-as-a-service', '#database', '#authentication', '#api', '#real-time'],
    features: [
      'Postgres database with real-time subscriptions',
      'Built-in authentication and authorization',
      'Auto-generated REST and GraphQL APIs',
      'Edge functions for serverless computing',
      'File storage and CDN',
      'Dashboard and SQL editor'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'n8n-workflow-automation',
    title: 'N8n',
    subtitle: 'Workflow Automation Platform',
    description: 'Secure and AI-native workflow automation tool designed for technical users to connect applications, automate tasks, and insert custom code.',
    icon: GitBranch,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#workflow-automation', '#integration', '#no-code', '#api-integration', '#developers'],
    features: [
      'Visual workflow builder',
      'Custom code node support',
      '400+ pre-built integrations',
      'Self-hosted and cloud options',
      'Advanced scheduling and triggers',
      'Error handling and debugging'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'cloud-infrastructure-optimization',
    title: 'Cloud Infrastructure Optimization',
    subtitle: 'Cost & Performance Management',
    description: 'Comprehensive cloud resource visibility and optimization platform that reduces costs by 30% without performance impact.',
    icon: Cloud,
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#cloud-optimization', '#cost-management', '#infrastructure', '#devops', '#monitoring'],
    features: [
      'Multi-cloud resource monitoring',
      'Automated cost optimization recommendations',
      'Performance impact analysis',
      'Right-sizing and scheduling suggestions',
      'Budget alerts and governance',
      'ROI tracking and reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-500'
  },
  {
    id: 'api-management-gateway',
    title: 'API Management Gateway',
    subtitle: 'Enterprise API Platform',
    description: 'Comprehensive API management solution for designing, publishing, securing, and analyzing APIs with advanced security and analytics.',
    icon: Server,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#api-management', '#microservices', '#security', '#analytics', '#enterprise'],
    features: [
      'API gateway and proxy',
      'OAuth 2.0 and JWT authentication',
      'Rate limiting and throttling',
      'API analytics and monitoring',
      'Developer portal and documentation',
      'Version control and lifecycle management'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'cicd-platform',
    title: 'CI/CD Platform',
    subtitle: 'Continuous Integration & Delivery',
    description: 'Advanced CI/CD pipeline automation that streamlines software delivery from code commit to production with comprehensive testing.',
    icon: GitBranch,
    image: 'https://images.unsplash.com/photo-1556075798-4825dfaaf498?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#cicd', '#devops', '#automation', '#testing', '#deployment'],
    features: [
      'Automated build and test pipelines',
      'Multi-environment deployment',
      'Quality gates and approvals',
      'Container and serverless support',
      'Integration with popular tools',
      'Deployment rollback and monitoring'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-500'
  },
  {
    id: 'container-management-platform',
    title: 'Container Management Platform',
    subtitle: 'Kubernetes Operations',
    description: 'Simplified container orchestration and management platform for deploying containerized applications across multiple environments.',
    icon: Container,
    image: 'https://images.unsplash.com/photo-1605810230434-7631ac76ec81?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#containers', '#kubernetes', '#orchestration', '#microservices', '#devops'],
    features: [
      'Kubernetes cluster management',
      'Container registry and scanning',
      'Service mesh integration',
      'Auto-scaling and load balancing',
      'Monitoring and logging',
      'Security policy enforcement'
    ],
    pricing: { setup: 30720, monthly: 1413 },
    color: 'from-teal-500 to-green-500'
  },
  {
    id: 'code-quality-security-scanning',
    title: 'Code Quality & Security Scanner',
    subtitle: 'Static Code Analysis',
    description: 'Automated code analysis platform that identifies bugs, vulnerabilities, and code smells during development with IDE integration.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#code-quality', '#security', '#static-analysis', '#vulnerability-scanning', '#compliance'],
    features: [
      'Static code analysis and SAST',
      'Vulnerability detection and remediation',
      'Code quality metrics and trends',
      'IDE and CI/CD integration',
      'Custom rule configuration',
      'Compliance reporting (OWASP, CWE)'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-red-500 to-pink-500'
  },
  {
    id: 'developer-experience-platform',
    title: 'Developer Experience Platform',
    subtitle: 'Productivity Analytics',
    description: 'Comprehensive developer productivity measurement and optimization platform with workflow insights and collaboration tools.',
    icon: Settings,
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#developer-productivity', '#analytics', '#team-collaboration', '#workflow-optimization', '#metrics'],
    features: [
      'Developer productivity metrics',
      'Code review and collaboration analytics',
      'Workflow bottleneck identification',
      'Team performance insights',
      'Integration with development tools',
      'Burnout and wellbeing indicators'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'serverless-framework',
    title: 'Serverless Framework',
    subtitle: 'Function-as-a-Service Platform',
    description: 'Complete serverless development platform enabling developers to build and deploy applications without infrastructure management.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?auto=format&fit=crop&w=800&q=80',
    category: 'Developer Tools',
    status: 'Available',
    businessTypes: ['#serverless', '#functions', '#auto-scaling', '#microservices', '#cloud-native'],
    features: [
      'Function deployment and management',
      'Auto-scaling and pay-per-use',
      'Event-driven architecture support',
      'Multiple cloud provider support',
      'Local development and testing',
      'Performance monitoring and logging'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-yellow-500 to-orange-500'
  }
];